/**
 * ApplicationContext - Manages currently selected application state
 *
 * Provides:
 * - Current application selection (from route params)
 * - Application data fetching
 * - Navigation helpers for application-scoped routes
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrganization } from './OrganizationContext';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { apiEndpoints, ApiError } from '../utils/api';
import { ApplicationResponse, ApplicationListResponse } from '../types/application';

const LAST_REPO_KEY_PREFIX = 'kolega_last_repository_id:';

/** Read the last-viewed repository id for an organization (used to resolve /repositories). */
export const getLastRepositoryId = (organizationId: string | null | undefined): string | null => {
  if (!organizationId) return null;
  try {
    return localStorage.getItem(`${LAST_REPO_KEY_PREFIX}${organizationId}`);
  } catch {
    return null;
  }
};

const setLastRepositoryId = (organizationId: string, applicationId: string) => {
  try {
    localStorage.setItem(`${LAST_REPO_KEY_PREFIX}${organizationId}`, applicationId);
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
};

export interface ApplicationContextType {
  // Application state
  currentApplication: ApplicationResponse | null;
  applicationId: string | null;
  isLoading: boolean;
  error: string | null;

  // Organization repository list (shared by the resolver and the sidebar switcher)
  applications: ApplicationResponse[];
  applicationsLoading: boolean;
  /** True once the repository list has been fetched for the current organization. */
  applicationsLoaded: boolean;

  // Navigation helpers
  navigateToApplication: (applicationId: string) => void;
  navigateToApplicationSection: (section: 'overview' | 'scans' | 'findings' | 'fixes' | 'settings') => void;
  clearApplication: () => void;

  // Refresh
  refreshApplication: () => Promise<void>;
  refreshApplications: () => Promise<void>;
}

const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

interface ApplicationProviderProps {
  children: ReactNode;
}

export const ApplicationProvider: React.FC<ApplicationProviderProps> = ({ children }) => {
  const { currentOrganization } = useOrganization();
  const api = useAuthenticatedApi();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentApplication, setCurrentApplication] = useState<ApplicationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applications, setApplications] = useState<ApplicationResponse[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  // The organization id the current `applications` list was loaded for (null until loaded).
  const [applicationsLoadedForOrg, setApplicationsLoadedForOrg] = useState<string | null>(null);

  // Extract applicationId from URL if we're on an application route
  const applicationId = useMemo(() => {
    const match = location.pathname.match(/^\/repositories\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Fetch application data when applicationId changes
  const fetchApplication = useCallback(async (appId: string) => {
    if (!currentOrganization?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get<ApplicationResponse>(
        apiEndpoints.applications.get(currentOrganization.id, appId)
      );
      setCurrentApplication(response);
      // Remember this as the organization's last-viewed repo only after a
      // confirmed, successful load, so a stale/cross-org id can never poison
      // the pointer used by the /repositories resolver.
      setLastRepositoryId(currentOrganization.id, appId);
    } catch (err: any) {
      // A 404 means the id in the URL doesn't belong to the active organization
      // — a stale/cross-org id left behind by a workspace switch or the browser
      // back button. Heal it by sending the user to the /repositories resolver,
      // which redirects to a valid repository for the active organization.
      if (err instanceof ApiError && err.status === 404) {
        setCurrentApplication(null);
        setError(null);
        navigate('/repositories', { replace: true });
        return;
      }
      console.error('Failed to fetch application:', err);
      setError(err.message || 'Failed to load repository');
      setCurrentApplication(null);
    } finally {
      setIsLoading(false);
    }
  }, [api, currentOrganization?.id, navigate]);

  // Refresh current application
  const refreshApplication = useCallback(async () => {
    if (applicationId) {
      await fetchApplication(applicationId);
    }
  }, [applicationId, fetchApplication]);

  // Fetch the organization's repository list (non-archived)
  const refreshApplications = useCallback(async () => {
    const orgId = currentOrganization?.id;
    if (!orgId) {
      setApplications([]);
      setApplicationsLoadedForOrg(null);
      return;
    }

    try {
      setApplicationsLoading(true);
      const response = await api.get<ApplicationListResponse>(
        apiEndpoints.applications.list(orgId)
      );
      setApplications(response.applications || []);
    } catch (err) {
      console.error('Failed to load applications:', err);
      setApplications([]);
    } finally {
      setApplicationsLoadedForOrg(orgId);
      setApplicationsLoading(false);
    }
  }, [api, currentOrganization?.id]);

  // Load the repository list when the organization changes
  useEffect(() => {
    refreshApplications();
  }, [refreshApplications]);

  // Load application when ID changes. The org's last-viewed repo is persisted
  // inside fetchApplication, but only after a successful load.
  useEffect(() => {
    if (applicationId && currentOrganization?.id) {
      fetchApplication(applicationId);
    } else {
      setCurrentApplication(null);
      setError(null);
    }
  }, [applicationId, currentOrganization?.id, fetchApplication]);

  // Navigation helpers
  const navigateToApplication = useCallback((appId: string) => {
    navigate(`/repositories/${appId}`);
  }, [navigate]);

  const navigateToApplicationSection = useCallback((
    section: 'overview' | 'scans' | 'findings' | 'fixes' | 'settings'
  ) => {
    if (!applicationId) return;

    const path = section === 'overview'
      ? `/repositories/${applicationId}`
      : `/repositories/${applicationId}/${section}`;

    navigate(path);
  }, [applicationId, navigate]);

  const clearApplication = useCallback(() => {
    setCurrentApplication(null);
    navigate('/repositories');
  }, [navigate]);

  const value = useMemo<ApplicationContextType>(() => ({
    currentApplication,
    applicationId,
    isLoading,
    error,
    applications,
    applicationsLoading,
    applicationsLoaded: !!currentOrganization?.id && applicationsLoadedForOrg === currentOrganization.id,
    navigateToApplication,
    navigateToApplicationSection,
    clearApplication,
    refreshApplication,
    refreshApplications,
  }), [
    currentApplication,
    applicationId,
    isLoading,
    error,
    applications,
    applicationsLoading,
    applicationsLoadedForOrg,
    currentOrganization?.id,
    navigateToApplication,
    navigateToApplicationSection,
    clearApplication,
    refreshApplication,
    refreshApplications,
  ]);

  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
};

export const useApplication = () => {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error('useApplication must be used within ApplicationProvider');
  }
  return context;
};

export default ApplicationContext;
